import React, { ReactNode, useEffect, useMemo, useRef } from 'react';
import cs from 'classnames';
import styles from './index.module.less';
import { Modal, ModalProps } from 'antd';
import IconFont from '../../Base/IconFont';
import V2KeyInProvincial from './components/V2KeyInProvincial';
import V2KeyInCity from './components/V2KeyInCity';
import { useMethods } from '@lhb/hook';

export interface V2ExtraCityProps {
  /**
   * @description 城市id
   */
  id: string | number;
  /**
   * @description 城市code
   */
  code?: string | number;
  /**
   * @description 城市名称
   */
  name: string;
  /**
   * @description 所属身份的拼音
   */
  pinyin?: string;
  /**
   * @description 所属身份的拼音的首字母大写
   */
  initial?: string;
}

export interface V2ExtraCitiesProps {
  /**
   * @description 标题
   */
  title: ReactNode;
  /**
   * @description 右侧插槽
   */
  extra?: ReactNode;
  /**
   * @description 城市列表
   */
  cities: Array<V2ExtraCityProps>;
}

export interface V2CityIndexBarProps {
  /**
   * @description 选中省份的key，默认是id，可选code
   * @default id
   */
  valueKey?: string;
  /**
   * @description 是否打开弹窗
   * @default false
   */
  open: boolean;
  /**
   * @description 操作弹窗后开关
   */
  setOpen: Function;
  /**
   * @description 选中值
   */
  checkedValue: any,
  /**
   * @description 选中值的操作函数
   */
  setCheckedValue: Function;
  /**
   * @description 额外城市列表
   */
  extraCities?: Array<V2ExtraCitiesProps>;
  /**
   * @description 选中回调
   */
  onConfirm?: (cityInfo: any) => void;
  /**
   * @description key聚合类型，可选 2：市纬度
   * @default 1
   */
  type?: number;
  /**
   * @description 外层弹窗类
   */
  wrapperClassName?: string;
  /**
   * @description 外层弹窗额外配置项，antd-modal
   */
  modalProps?: ModalProps;
  /**
   * @description 关闭回调
   */
  onCancel?: Function;
  /**
   * @description 是否允许选择港澳台
   * @default false
   */
  allowSpecialRegion?: boolean;
  /**
   * @description 是否完全受控，仅 type=1时生效
   * @default false
   */
  checkStrictly?: boolean;
  /**
   * @description 数据组装完成后的回调，默认是首次open此组件弹窗，如果希望立刻执行此回调，请设置modalProps.forceRender = true
   */
  onDataLoaded?: (list: any[], treeData?: any[]) => void;
  /**
   * @description 选择项点击执行选中前的回调，如果返回true就阻止选中
   */
  beforeCheck?: (item: any) => Promise<boolean>;
}

/**
* @description 便捷文档地址
* @see https://reactpc.lanhanba.net/components/form/v2city-index-bar
*/
const V2CityIndexBar: React.FC<V2CityIndexBarProps> = ({
  valueKey = 'id',
  open = false,
  setOpen,
  checkedValue,
  setCheckedValue,
  extraCities,
  onConfirm,
  type = 1,
  wrapperClassName,
  modalProps,
  onCancel,
  allowSpecialRegion = false,
  checkStrictly = false,
  onDataLoaded,
  beforeCheck,
}) => {
  const keyRef: any = useRef(null);
  const methods = useMethods({
    onCancel() {
      setOpen(false);
      onCancel?.();
    },
  });
  const renderTitle = useMemo(() => {
    return (
      <div className={cs(styles.v2CityIndexBarModalTitle, 'v2CityIndexBarModalTitle')}>
        <div>选择{type === 1 && checkStrictly ? '省' : '城'}市</div>
        {
          checkedValue?.name && (
            <div className={cs(styles.v2CityIndexBarActive, 'v2CityIndexBarActive')}>
              <IconFont iconHref='pc-common-icon-ic_map_white_one' style={{ color: '#0065F2', fontSize: 13, marginRight: 4 }}/>
              <span>{checkedValue.name}</span>
            </div>
          )
        }
      </div>
    );
  }, [checkedValue]);
  useEffect(() => {
    if (open) { // 打开就立刻重置选中效果
      keyRef.current.init(checkedValue);
    }
  }, [open]);
  const CustomCom = type === 1 ? V2KeyInProvincial : V2KeyInCity;
  return (
    <Modal
      width={640}
      title={renderTitle}
      open={open}
      className={cs(styles.v2CityIndexBar, wrapperClassName)}
      footer={null}
      onCancel={methods.onCancel}
      closable={false}
      {...modalProps}
    >
      <CustomCom
        ref={keyRef}
        valueKey={valueKey}
        setOpen={setOpen}
        setCheckedValue={setCheckedValue}
        extraCities={extraCities}
        onConfirm={onConfirm}
        allowSpecialRegion={allowSpecialRegion}
        checkStrictly={checkStrictly}
        onDataLoaded={onDataLoaded}
        beforeCheck={beforeCheck}
      />
    </Modal>
  );
};

export default V2CityIndexBar;
